import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { PagePreviewPopover, type PreviewAnchor, type PreviewablePage } from './PagePreviewPopover';

type HoverApi = {
  show: (page: PreviewablePage, anchor: PreviewAnchor) => void;
  hide: () => void;
};

const PagePreviewHoverContext = createContext<HoverApi>({ show: () => {}, hide: () => {} });

export function usePagePreviewHover() {
  return useContext(PagePreviewHoverContext);
}

// Single shared hover popover for the whole preview grid. The `api` value is stable
// (memoised with no deps) so cards consuming it never re-render when the hovered
// page changes — only this provider and the popover do. `children` is passed through
// untouched, so it keeps its element identity across hover state changes and React
// skips re-rendering the (potentially huge) grid below.
export function PagePreviewHoverProvider({
  children,
  visibleIds,
  dismissKey,
}: {
  children: ReactNode;
  /** Ids currently rendered. The popover hides only when the hovered page leaves this
   * set (filtered out, cluster nav, removed) — so it does NOT flicker off on a filter
   * tick that keeps the still-hovered card present. */
  visibleIds?: Set<string>;
  /** Extra blunt dismiss for changes that keep the id but move/unmount the anchor
   * (e.g. a grid⇄table view toggle), where the captured rect is no longer valid. */
  dismissKey?: unknown;
}) {
  const [hovered, setHovered] = useState<{ page: PreviewablePage; anchor: PreviewAnchor } | null>(null);

  const api = useMemo<HoverApi>(
    () => ({
      show: (page, anchor) => setHovered({ page, anchor }),
      hide: () => setHovered(null),
    }),
    [],
  );

  // The anchor rect is captured once on mouseenter, so scrolling the previews pane
  // moves the card out from under a fixed popover (and may not fire mouseleave).
  // Dismiss on any scroll — capture phase so the inner overflow-y-auto pane counts.
  useEffect(() => {
    if (!hovered) return;
    const onScroll = () => setHovered(null);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [hovered]);

  // Hide when the hovered card unmounts WITHOUT a mouseleave — i.e. it left the
  // rendered set. Membership-scoped so a filter/search update that keeps the hovered
  // card under the pointer does not dismiss it.
  useEffect(() => {
    if (hovered && visibleIds && !visibleIds.has(hovered.page.id)) setHovered(null);
  }, [hovered, visibleIds]);

  useEffect(() => {
    setHovered(null);
  }, [dismissKey]);

  return (
    <PagePreviewHoverContext.Provider value={api}>
      {children}
      {hovered && <PagePreviewPopover page={hovered.page} anchor={hovered.anchor} />}
    </PagePreviewHoverContext.Provider>
  );
}
